import React, { CSSProperties, Ref } from 'react';
import { defined } from '@unifocus/js-utils';
import { convertSize } from '../../../utils/StyleUtils';
import { extractDomProps } from '../../../utils/DomPropsExtractor';

export type PaddingType = boolean | 'small' | 'medium' | 'large';

/**
 * {@docCategory Panel}
 */
export interface PanelProps {
   /**
    * Determines if the panel has a border around it.
    *
    * @defaultvalue false
    */
   border?: boolean;

   /**
    * Determines if the panel has padding around the content.
    *
    * @defaultvalue false
    */
   padding?: PaddingType;

   /**
    * Determines if the panel has left padding
    *
    * @defaultvalue false
    */
   paddingLeft?: PaddingType;

   /**
    * Determines if the panel has right padding
    *
    * @defaultvalue false
    */
   paddingRight?: PaddingType;

   /**
    * Determines if the panel has top padding
    *
    * @defaultvalue false
    */
   paddingTop?: PaddingType;

   /**
    * Determines if the panel has bottom padding
    *
    * @defaultvalue false
    */
   paddingBottom?: PaddingType;

   /**
    * Used to enable the built in overflow configuration for the content area.
    * @defaultvalue false
    */
   scroll?: boolean;

   /**
    * Determines if the panel will fill its parent's width and height.
    *
    * @defaultvalue false
    */
   fill?: boolean;

   /**
    * Determines if the panel will fill its parent's width.
    *
    * @defaultvalue false
    */
   horizontalFill?: boolean;

   /**
    * Determines if the panel will fill its parent's height.
    *
    * @defaultvalue false
    */
   verticalFill?: boolean;

   /**
    * The width of the panel. It can be specified as a string, following the standard css values for
    * sizes. It can also be specified as a number. When specified as a number, if the value is greater
    * than zero then it will be converted to a string with "px" appended.
    *
    * @defaultvalue '100%' if horizontalFill is true; otherwise 'max-content'
    */
   width?: string | number;

   /**
    * The height of the panel. It can be specified as a string, following the standard css values for
    * sizes. It can also be specified as a number. When specified as a number, if the value is greater
    * than zero then it will be converted to a string with "px" appended.
    *
    * @defaultvalue '100%' if verticalFill is true; otherwise 'max-content'
    */
   height?: string | number;

   /**
    * The minimum width of the panel. It can be specified as a string, following the standard css values for
    * sizes. It can also be specified as a number. When specified as a number, if the value is greater
    * than zero then it will be converted to a string with "px" appended.
    *
    * @defaultvalue 0
    */
   minWidth?: string | number;

   /**
    * The mimimum height of the panel. It can be specified as a string, following the standard css values for
    * sizes. It can also be specified as a number. When specified as a number, if the value is greater
    * than zero then it will be converted to a string with "px" appended.
    *
    * @defaultvalue 0
    */
   minHeight?: string | number;

   /**
    * The maximum width of the panel. It can be specified as a string, following the standard css values for
    * sizes. It can also be specified as a number. When specified as a number, if the value is greater
    * than zero then it will be converted to a string with "px" appended.
    *
    * @defaultvalue undefined
    */
   maxWidth?: string | number;

   /**
    * The maximum height of the panel. It can be specified as a string, following the standard css values for
    * sizes. It can also be specified as a number. When specified as a number, if the value is greater
    * than zero then it will be converted to a string with "px" appended.
    *
    * @defaultvalue undefined
    */
   maxHeight?: string | number;

   /**
    * Sets a className on the panel.
    */
   className?: string;

   /**
    * Sets styles on the panel.
    */
   style?: CSSProperties;

   /**
    * Sets the reference property
    */
   ref?: Ref<HTMLDivElement>;

   /**
    * Sets the id of the element in the DOM.
    */
   id?: string;

   /**
    * Called when the panel is clicked.
    */
   onClick?: (event: React.MouseEvent<HTMLElement>) => void;
}

export const Panel: React.FC<React.PropsWithChildren<PanelProps>> = React.forwardRef<HTMLDivElement>(
   (props: React.PropsWithChildren<PanelProps>, ref: React.Ref<HTMLDivElement>) => {
      const style = getStyle(props);
      const classNames = getClassNames(props);

      return (
         <div
            {...extractDomProps(props,['width','height'])}
            onClick={props.onClick}
            className={classNames}
            style={style}
            ref={ref}
            id={props.id}
         >
            {props.children}
         </div>
      );
   });

const getStyle = (props: PanelProps) => {
   const horizontalFill = defined(props.horizontalFill) ? props.horizontalFill : props.fill;
   const verticalFill = defined(props.verticalFill) ? props.verticalFill : props.fill;
   const style: CSSProperties = {
      height: convertSize(props.height) || (verticalFill ? '100%' : 'max-content'),
      width: convertSize(props.width) || (horizontalFill ? '100%' : 'max-content'),
      maxHeight: convertSize(props.maxHeight) || (props.style ? props.style.maxHeight : undefined),
      maxWidth: convertSize(props.maxWidth) || (props.style ? props.style.maxWidth : undefined),
      ...props.style
   };

   if (props.minWidth !== undefined && defined(props.minWidth)) {
      style.minWidth = convertSize(props.minWidth);
   }

   if (props.minHeight !== undefined && defined(props.minHeight)) {
      style.minHeight = convertSize(props.minHeight);
   }

   return style;
};

const getPaddingClassName = (paddingProp?: PaddingType, paddingString?: string) => {
   if (paddingProp === true) {
      return ` uf-panel-padding${paddingString ? "-" + paddingString : ''}`;
   }
   else if (paddingProp) {
      return ` uf-panel-padding${paddingString ? "-" + paddingString : ''}-${paddingProp}`;
   }
   else if (paddingProp===false) {
      return ` uf-panel-padding${paddingString ? "-" + paddingString : ''}-none`;
   }
   return '';
};

const getClassNames = (props: PanelProps) => {
   let classNames = 'uf-panel';

   if (props.border) {
      classNames += ' uf-panel-border';
   }

   if (props.scroll) {
      classNames += ' uf-panel-scroll';
   }

   if (props.onClick) {
      classNames += ' uf-panel-cursor-pointer';
   }

   classNames += ` ${getPaddingClassName(props.padding)}`.trimRight();
   classNames += ` ${getPaddingClassName(props.paddingLeft, 'left')}`.trimRight();
   classNames += ` ${getPaddingClassName(props.paddingRight, 'right')}`.trimRight();
   classNames += ` ${getPaddingClassName(props.paddingTop, 'top')}`.trimRight();
   classNames += ` ${getPaddingClassName(props.paddingBottom, 'bottom')}`.trimRight();

   if (props.className) {
      classNames += ' ' + props.className;
   }

   return classNames;
};
